import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './styles/design-system.css';
import './i18n/index';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AppProvider } from './context/AppContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './components/UserInterface/ConfirmDialog';
import { TenantProvider, useTenant } from './context/TenantContext';
import { UiLabelsProvider } from './hooks/useUiLabels';
import { ThemeProvider } from './context/ThemeContext';
import { CREATION_TENANT_RESERVEE_AU_MASTER } from './constants/creationTenant';

const Login              = lazy(() => import('./pages/Login'));
const Dashboard          = lazy(() => import('./pages/Dashboard'));
const PortailParents     = lazy(() => import('./pages/PortailParents'));
const PublicPortfolioView = lazy(() => import('./pages/PublicPortfolioView'));
const PublicVerifyView    = lazy(() => import('./pages/PublicVerifyView'));
const PublicInscriptionView = lazy(() => import('./pages/PublicInscriptionView'));
const PublicCandidatureView = lazy(() => import('./pages/PublicCandidatureView'));
const DeviceGate          = lazy(() => import('./components/security/DeviceGate'));
const SignupPage          = lazy(() => import('./pages/public/SignupPage'));
const ForgotPasswordPage  = lazy(() => import('./pages/public/ForgotPasswordPage'));
const ResetPasswordPage   = lazy(() => import('./pages/public/ResetPasswordPage'));
const AuthCallbackPage    = lazy(() => import('./pages/public/AuthCallbackPage'));
const OnboardingPage      = lazy(() => import('./pages/public/OnboardingPage'));
const CreationSuspendue   = lazy(() => import('./pages/public/CreationSuspendue'));
const InviteAcceptPage    = lazy(() => import('./pages/public/InviteAcceptPage'));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { tenantLoading } = useTenant();

  if (loading || tenantLoading) return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh', 
      flexDirection: 'column', 
      gap: 12,
      background: 'var(--background)'
    }}>
      <div style={{
        width: 36, 
        height: 36, 
        borderRadius: '50%',
        border: '3px solid #334155',
        borderTopColor: 'var(--color-primary)',
        animation: 'spin 0.8s linear infinite'
      }} />
      <span style={{ fontSize: 13, color: 'var(--color-text-3)', fontWeight: 600 }}>Initialisation sécurisée...</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!user) return <Navigate to="/login" />;

  return children;
};


const App = () => {
  return (
    <TenantProvider>
    <ToastProvider>
    <ConfirmProvider>
    <AppProvider>
      <AuthProvider>
        <UiLabelsProvider>
        <ThemeProvider>
          <Router>
            <Suspense fallback={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--background)' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #334155', borderTopColor: 'var(--color-primary)', animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            }>
              <Routes>
                {/* Funnel public Free — landing + signup/login (PARCOURS_INSCRIPTION_FREE.md) */}
                <Route path="/login" element={<Login />} />
                {/* Création de tenant réservée au Master jusqu'à nouvel ordre (src/constants/creationTenant.js) */}
                <Route path="/signup" element={CREATION_TENANT_RESERVEE_AU_MASTER ? <CreationSuspendue /> : <SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/onboarding" element={CREATION_TENANT_RESERVEE_AU_MASTER ? <CreationSuspendue /> : <OnboardingPage />} />
                {/* Acceptation d'invitation collaborateur — rôle pré-assigné (doctrine §1) */}
                <Route path="/invite/:token" element={<InviteAcceptPage />} />
                {/* Portail Parents — accès public, sans connexion admin requise */}
                <Route path="/parents" element={<PortailParents />} />
                <Route path="/parents/*" element={<PortailParents />} />

                {/* e-Portfolio — accès public pour les recruteurs */}
                <Route path="/portfolio/:studentId" element={<PublicPortfolioView />} />

                {/* Vérification QR — accès public sans connexion */}
                <Route path="/verify/:hash" element={<PublicVerifyView />} />
                <Route path="/inscription/:slug" element={<PublicInscriptionView />} />
                <Route path="/candidature/:slug" element={<PublicCandidatureView />} />

                <Route
                  path="/dashboard/*"
                  element={
                    <ProtectedRoute>
                      <DeviceGate>
                        <Dashboard />
                      </DeviceGate>
                    </ProtectedRoute>
                  }
                />
                {/* Lot 4 : landing marketing supprimée (site vitrine séparé à venir) —
                    la racine mène directement à l'application. */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </Suspense>
          </Router>
        </ThemeProvider>
        </UiLabelsProvider>
      </AuthProvider>
    </AppProvider>
    </ConfirmProvider>
    </ToastProvider>
    </TenantProvider>
  );
};

export default App;
